import { Loader2, Plug, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useReportPageReady } from "@/hooks/useReportPageReady";
import {
  useAvailableIntegrations,
  useConnectedIntegrations,
  useCreateIntegration,
} from "../../../hooks/useIntegrations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PermissionTooltip } from "@/components/PermissionGate";
import { usePermissions } from "@/contexts/usePermissions";
import { ConfigurationFieldRenderer } from "../../../ui/configurationFieldRenderer";
import type { IntegrationsIntegrationDefinition } from "../../../api-client/types.gen";
import { getApiErrorMessage } from "@/lib/errors";
import { getUsageLimitNotice, getUsageLimitToastMessage } from "@/lib/usageLimits";
import { getIntegrationTypeDisplayName } from "@/lib/integrationDisplayName";
import { Icon } from "@/components/Icon";
import { UsageLimitAlert } from "@/components/UsageLimitAlert";
import { showErrorToast } from "@/lib/toast";
import { IntegrationIcon } from "@/ui/componentSidebar/integrationIcons";
import { IntegrationInstructions } from "@/ui/IntegrationInstructions";
import { Alert, AlertDescription, AlertTitle } from "@/ui/alert";
import { analytics } from "@/lib/analytics";
import { isCapabilityBasedIntegrationDefinition } from "@/lib/integrations";
import { posthog, isPostHogEnabled } from "@/posthog";
import { cn } from "@/lib/utils";
import {
  settingsEmptyStateIconClassName,
  settingsEmptyStateTitleClassName,
  settingsModalClassName,
  settingsPanelClassName,
} from "./settingsPageStyles";

const INTEGRATION_SURVEY_NAME = "Integration Survey";

interface IntegrationsProps {
  organizationId: string;
}

export function Integrations({ organizationId }: IntegrationsProps) {
  usePageTitle(["Integrations"]);
  const navigate = useNavigate();
  const { canAct, isLoading: permissionsLoading } = usePermissions();
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationsIntegrationDefinition | null>(null);
  const [integrationName, setIntegrationName] = useState("");
  const [configuration, setConfiguration] = useState<Record<string, unknown>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const [isIntegrationSurveyActive, setIsIntegrationSurveyActive] = useState(false);
  const canCreateIntegrations = canAct("integrations", "create");
  const canUpdateIntegrations = canAct("integrations", "update");

  useEffect(() => {
    if (!isPostHogEnabled) return;

    posthog.getSurveys((surveys) => {
      const isActive = surveys.some(
        (survey) => survey.name === INTEGRATION_SURVEY_NAME && survey.start_date != null && survey.end_date == null,
      );
      setIsIntegrationSurveyActive(isActive);
    }, true);
  }, []);

  const { data: availableIntegrations = [], isLoading: loadingAvailable } = useAvailableIntegrations();
  const { data: organizationIntegrations = [], isLoading: loadingInstalled } = useConnectedIntegrations(organizationId);
  const createIntegrationMutation = useCreateIntegration(organizationId, "integrations_page");

  const isLoading = loadingAvailable || loadingInstalled;

  useReportPageReady(!isLoading && !permissionsLoading);

  const integrationNames = useMemo(() => {
    return new Set(
      organizationIntegrations.map((integration) => integration.metadata?.name?.trim()).filter(Boolean) as string[],
    );
  }, [organizationIntegrations]);
  const connectedInstancesByProvider = useMemo(() => {
    const groups = new Map<string, typeof organizationIntegrations>();

    organizationIntegrations.forEach((integration) => {
      const provider = integration.metadata?.integrationName;
      if (!provider) return;
      const current = groups.get(provider) || [];
      current.push(integration);
      groups.set(provider, current);
    });

    return groups;
  }, [organizationIntegrations]);
  const integrationCatalog = useMemo(() => {
    const catalogByProvider = new Map<
      string,
      {
        providerName: string;
        providerLabel: string;
        integrationDef: IntegrationsIntegrationDefinition | null;
        instances: typeof organizationIntegrations;
      }
    >();

    availableIntegrations.forEach((integrationDef) => {
      const providerName = integrationDef.name || "";
      const providerLabel =
        integrationDef.label ||
        getIntegrationTypeDisplayName(undefined, integrationDef.name) ||
        integrationDef.name ||
        "Integration";
      const instances = [...(connectedInstancesByProvider.get(providerName) || [])].sort((a, b) =>
        (a.metadata?.name || providerLabel).localeCompare(b.metadata?.name || providerLabel),
      );

      catalogByProvider.set(providerName, {
        providerName,
        providerLabel,
        integrationDef,
        instances,
      });
    });

    connectedInstancesByProvider.forEach((instances, providerName) => {
      if (catalogByProvider.has(providerName)) {
        return;
      }

      const providerLabel = getIntegrationTypeDisplayName(undefined, providerName) || providerName || "Integration";
      const sortedInstances = [...instances].sort((a, b) =>
        (a.metadata?.name || providerLabel).localeCompare(b.metadata?.name || providerLabel),
      );

      catalogByProvider.set(providerName, {
        providerName,
        providerLabel,
        integrationDef: null,
        instances: sortedInstances,
      });
    });

    return [...catalogByProvider.values()].sort((a, b) => {
      const aHasInstances = a.instances.length > 0;
      const bHasInstances = b.instances.length > 0;
      if (aHasInstances !== bHasInstances) {
        return aHasInstances ? -1 : 1;
      }
      return a.providerLabel.localeCompare(b.providerLabel);
    });
  }, [availableIntegrations, connectedInstancesByProvider]);
  const filteredIntegrationCatalog = useMemo(() => {
    const normalizedQuery = filterQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return integrationCatalog;
    }

    return integrationCatalog.filter((item) => {
      const providerText = [item.providerLabel, item.providerName, item.integrationDef?.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (providerText.includes(normalizedQuery)) {
        return true;
      }

      return item.instances.some((instance) =>
        (instance.metadata?.name || instance.metadata?.integrationName || "").toLowerCase().includes(normalizedQuery),
      );
    });
  }, [filterQuery, integrationCatalog]);

  const selectedInstructions = useMemo(() => {
    return selectedIntegration?.instructions?.trim() ?? "";
  }, [selectedIntegration?.instructions]);

  const getNextIntegrationName = (baseName?: string) => {
    const normalizedBaseName = baseName?.trim() || "integration";
    if (!integrationNames.has(normalizedBaseName)) {
      return normalizedBaseName;
    }

    let suffix = 2;
    let candidate = `${normalizedBaseName}-${suffix}`;
    while (integrationNames.has(candidate)) {
      suffix += 1;
      candidate = `${normalizedBaseName}-${suffix}`;
    }

    return candidate;
  };

  const handleConnectClick = (integration: IntegrationsIntegrationDefinition) => {
    if (!canCreateIntegrations) return;

    if (isCapabilityBasedIntegrationDefinition(integration)) {
      if (!integration.name) return;
      analytics.integrationConnectStart(integration.name, "integrations_page", organizationId);
      navigate(`/${organizationId}/settings/integrations/${integration.name}/setup`);
      return;
    }

    setSelectedIntegration(integration);
    setIntegrationName(getNextIntegrationName(integration.name));
    setConfiguration({});
    setIsModalOpen(true);
    analytics.integrationConnectStart(integration.name ?? "", "integrations_page", organizationId);
  };
  const handleConnect = async () => {
    if (!canCreateIntegrations) return;
    if (!selectedIntegration?.name) return;

    try {
      const result = await createIntegrationMutation.mutateAsync({
        integrationName: selectedIntegration.name,
        name: integrationName,
        configuration,
      });
      setIsModalOpen(false);
      setSelectedIntegration(null);
      setIntegrationName("");
      setConfiguration({});

      // Redirect to the integration details page
      if (result.data?.integration?.metadata?.id) {
        navigate(`/${organizationId}/settings/integrations/${result.data.integration.metadata.id}`);
      }
    } catch (_error) {
      showErrorToast(getUsageLimitToastMessage(_error, "Failed to create integration"));
    }
  };

  const handleRequestIntegration = () => {
    analytics.integrationRequested(organizationId);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedIntegration(null);
    setIntegrationName("");
    setConfiguration({});
    createIntegrationMutation.reset();
  };

  const createIntegrationNotice = createIntegrationMutation.isError
    ? getUsageLimitNotice(createIntegrationMutation.error, organizationId)
    : null;

  if (isLoading) {
    return (
      <div className="pt-6">
        <div className="flex justify-center items-center h-32">
          <p className="text-gray-500 dark:text-gray-400">Loading integrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-6">
      <div className="relative mb-4">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
        <Input
          type="text"
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          placeholder="Filter integrations..."
          className="pl-9 pr-9"
        />
        {filterQuery.length > 0 ? (
          <button
            type="button"
            onClick={() => setFilterQuery("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            aria-label="Clear filter"
          >
            <X className="w-4 h-4" />
          </button>
        ) : null}
      </div>
      {filteredIntegrationCatalog.length === 0 ? (
        <div className="py-12 text-center">
          <Plug className={cn("mx-auto mb-2 h-6 w-6", settingsEmptyStateIconClassName)} />
          <p className={settingsEmptyStateTitleClassName}>
            {integrationCatalog.length === 0 ? "No integrations available." : "No integrations match your filter."}
          </p>
          {isIntegrationSurveyActive ? (
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Can't find your integration?{" "}
              <button
                type="button"
                onClick={handleRequestIntegration}
                className="text-blue-600 hover:underline dark:text-blue-400 font-medium"
              >
                Request it
              </button>
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredIntegrationCatalog.map((item) => {
            const connectedCount = item.instances.length;

            return (
              <div key={item.providerName} className={settingsPanelClassName}>
                <div className="p-4 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 items-center justify-center">
                      <IntegrationIcon
                        integrationName={item.providerName}
                        iconSlug={item.integrationDef?.icon}
                        className="w-8 h-8 text-gray-500 dark:text-gray-400"
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{item.providerLabel}</h3>
                      {item.integrationDef?.description ? (
                        <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-400">
                          {item.integrationDef?.description}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <PermissionTooltip
                    allowed={Boolean(item.integrationDef) && (canCreateIntegrations || permissionsLoading)}
                    message={
                      item.integrationDef
                        ? "You don't have permission to connect integrations."
                        : "This integration provider is no longer available for new connections."
                    }
                  >
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => {
                        if (!item.integrationDef) return;
                        handleConnectClick(item.integrationDef);
                      }}
                      className="self-start"
                      disabled={!item.integrationDef || !canCreateIntegrations}
                    >
                      {item.integrationDef ? "Connect" : "Unavailable"}
                    </Button>
                  </PermissionTooltip>
                </div>
                {item.instances.length > 0 ? (
                  <div className="pr-4 pb-4 pl-[60px]">
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      {connectedCount} connected instance{connectedCount === 1 ? "" : "s"}
                    </p>
                    {item.instances.map((integration, index) => {
                      const integrationDisplayName = integration.metadata?.name;
                      const statusLabel = integration.status?.state
                        ? integration.status.state.charAt(0).toUpperCase() + integration.status.state.slice(1)
                        : "Unknown";

                      return (
                        <div
                          key={integration.metadata?.id}
                          className={`flex items-center gap-2 py-1.5 border-t border-gray-200 dark:border-gray-700/70 ${index === 0 ? "mt-1" : ""}`}
                        >
                          <Plug
                            className={`w-4 h-4 shrink-0 ${
                              integration.status?.state === "ready"
                                ? "text-green-500"
                                : integration.status?.state === "error"
                                  ? "text-red-500"
                                  : "text-amber-600"
                            }`}
                          />
                          <span
                            className={cn(
                              "inline-flex w-16 items-center justify-start rounded text-xs font-medium",
                              integration.status?.state === "ready"
                                ? "bg-white text-green-500 dark:bg-green-300 dark:text-green-950"
                                : integration.status?.state === "error"
                                  ? "bg-white text-red-500 dark:bg-red-300 dark:text-red-950"
                                  : "bg-white text-amber-600 dark:bg-amber-300 dark:text-amber-950",
                            )}
                          >
                            {statusLabel}
                          </span>
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                            {integrationDisplayName}
                          </p>
                          <div className="ml-auto flex items-center gap-4">
                            <PermissionTooltip
                              allowed={canUpdateIntegrations || permissionsLoading}
                              message="You don't have permission to update integrations."
                            >
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (!canUpdateIntegrations) return;
                                  const providerName = integration.metadata?.integrationName;
                                  if (providerName && integration.status?.setupState?.currentStep) {
                                    navigate(`/${organizationId}/settings/integrations/${providerName}/setup`, {
                                      state: { integrationId: integration.metadata?.id },
                                    });
                                    return;
                                  }

                                  navigate(`/${organizationId}/settings/integrations/${integration.metadata?.id}`, {
                                    state: { tab: "configuration" },
                                  });
                                }}
                                disabled={!canUpdateIntegrations}
                              >
                                Configure
                              </Button>
                            </PermissionTooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
          {isIntegrationSurveyActive ? (
            <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
              Can't find your integration?{" "}
              <button
                type="button"
                onClick={handleRequestIntegration}
                className="text-blue-600 hover:underline dark:text-blue-400 font-medium"
              >
                Request it
              </button>
            </p>
          ) : null}
        </div>
      )}

      {/* Connect Modal */}
      {isModalOpen &&
        selectedIntegration &&
        (() => {
          const integrationTypeName = selectedIntegration.name;
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className={cn(settingsModalClassName, "max-h-[80vh] max-w-2xl overflow-y-auto")}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <IntegrationIcon
                        integrationName={integrationTypeName}
                        iconSlug={selectedIntegration.icon}
                        className="w-6 h-6 text-gray-500 dark:text-gray-400"
                      />
                      <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                        Connect {selectedIntegration.label || selectedIntegration.name}
                      </h3>
                    </div>
                    <button
                      onClick={handleCloseModal}
                      className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-300"
                      disabled={createIntegrationMutation.isPending}
                    >
                      <Icon name="x" size="sm" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {selectedInstructions && <IntegrationInstructions description={selectedInstructions} />}
                    {/* Integration Name Field */}
                    <div>
                      <Label className="text-gray-800 dark:text-gray-100 mb-2">
                        Integration Name
                        <span className="ml-1 text-gray-800 dark:text-gray-100">*</span>
                      </Label>
                      <Input
                        type="text"
                        value={integrationName}
                        onChange={(e) => setIntegrationName(e.target.value)}
                        placeholder="e.g., my-app-integration"
                        required
                        disabled={!canCreateIntegrations}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        A unique name for this integration
                      </p>
                    </div>

                    {/* Configuration Fields */}
                    {selectedIntegration.configuration && selectedIntegration.configuration.length > 0 && (
                      <div className="space-y-4">
                        {selectedIntegration.configuration
                          .filter((field) => Boolean(field.name))
                          .map((field) => (
                            <ConfigurationFieldRenderer
                              key={field.name!}
                              field={field}
                              value={configuration[field.name!]}
                              onChange={(value) => setConfiguration({ ...configuration, [field.name!]: value })}
                              allValues={configuration}
                              organizationId={organizationId}
                            />
                          ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-start gap-3 mt-6">
                    <Button
                      color="blue"
                      onClick={handleConnect}
                      disabled={
                        createIntegrationMutation.isPending || !integrationName?.trim() || !canCreateIntegrations
                      }
                      className="flex items-center gap-2"
                    >
                      {createIntegrationMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        "Connect"
                      )}
                    </Button>
                    <Button variant="outline" onClick={handleCloseModal} disabled={createIntegrationMutation.isPending}>
                      Cancel
                    </Button>
                  </div>

                  {createIntegrationMutation.isError && createIntegrationNotice ? (
                    <UsageLimitAlert notice={createIntegrationNotice} className="mt-4" />
                  ) : null}
                  {createIntegrationMutation.isError && !createIntegrationNotice ? (
                    <Alert variant="destructive" className="mt-4">
                      <AlertTitle>Unable to create integration</AlertTitle>
                      <AlertDescription>
                        Failed to create integration: {getApiErrorMessage(createIntegrationMutation.error)}
                      </AlertDescription>
                    </Alert>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
